/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import {
  Component,
  OnInit,
  inject,
  HostListener,
  ElementRef,
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core'
import { BoletoModalService } from '../services/boleto-modal.service'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { BoletoPreview } from '../models/boleto-preview'

@Component({
  selector: 'app-boleto-modal',
  templateUrl: './boleto-modal.component.html',
  styleUrl: './boleto-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoletoModalComponent implements OnInit, AfterViewChecked {
  private el = inject(ElementRef)
  private cdr = inject(ChangeDetectorRef)
  private sanitizer = inject(DomSanitizer)
  private previousFocus: HTMLElement | null = null
  private needsFocus = false
  private boletoModalService = inject(BoletoModalService)

  boletoPreview?: BoletoPreview
  exibir: boolean = false
  trustedUrl!: SafeUrl

  ngOnInit(): void {
    this.subscribeToModal()
  }

  subscribeToModal() {
    this.boletoModalService.boletoModalEventsArquivo.subscribe(boletoPreview => {
      this.boletoPreview = boletoPreview
      this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(boletoPreview.url)
      this.toggle()
      this.cdr.markForCheck()
    })
  }

  ngAfterViewChecked(): void {
    if (this.needsFocus && this.exibir) {
      this.needsFocus = false
      const first = this.el.nativeElement.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      first?.focus()
    }
  }

  @HostListener('keydown', ['$event'])
  onTab(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.exibir) return
    const focusable = this.el.nativeElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.exibir) this.toggle()
  }

  toggle(): void {
    if (!this.exibir) {
      this.previousFocus = document.activeElement as HTMLElement
      this.needsFocus = true
    } else {
      this.previousFocus?.focus()
    }
    this.exibir = !this.exibir
  }
}
